import { RefObject } from "react";
import axios from "axios"
import { CompanyIdMatch, ContactIdMatch } from "@hubspot/calling-extensions-sdk";
import styled from "styled-components";
import Button from 'react-bootstrap/Button'
import { LoadingButton, PressButton } from "../../Components/LoadingButton.tsx";
import { proxyUrl, AssociateHsObject, DisassociateHsObject, ChangeAssociationsHsObject } from "./Utils.jsx";
import { IsEmptyString } from "../../Components/GeneralFunctions.jsx"
import { Translate } from "../../Components/Translator"

const TextArea = styled.textarea`
  padding: 10px;
  margin-top: 3px;
  font-family: Lexend;
  color: black;
  border: 1px solid #cbd6e2;
  border-radius: 5px;
  ::placeholder {
    color: #99acc2;
  }
  resize: none;
  width: 80%;
`;

//const hubspotBroadcastChannelEmbedded = new BroadcastChannel("cloudctiwebclient2024_hubspot_embedded");

function CallNotesElement (brand: any, cti: any, incomingMatches: (ContactIdMatch | CompanyIdMatch)[], textAreaRef: RefObject<HTMLTextAreaElement>, engagementIdRef: any, setForceRepaint: CallableFunction) {
    const previousNotes = localStorage.getItem("hb_stashedNotes");
    const saveCallButton = <div>
        {LoadingButton(Translate("Save call"), async () => {
            const localStorageSnapshot = {
                hb_selectedMatchObjectId: localStorage.getItem("hb_selectedMatchObjectId"),
                hb_lastAssociatedCallId: localStorage.getItem("hb_lastAssociatedCallId"),
                hb_lastAssociatedObjectId: localStorage.getItem("hb_lastAssociatedObjectId"),
                newestcallevent: localStorage.getItem("newestcallevent"),
            };
            const notesSnapshot = textAreaRef.current ? textAreaRef.current.value : null;
            return new Promise(resolve => setTimeout(() => { 
                onSaveCall(cti, incomingMatches, notesSnapshot, engagementIdRef.current, undefined, localStorageSnapshot); 
                resolve();
                const newestCallstr = localStorageSnapshot.newestcallevent;
                var newestCallObject;
                if (newestCallstr != undefined) {
                    newestCallObject = JSON.parse(newestCallstr);
                    if (localStorage.getItem("hubspot_setting_collapse_on_save") === "1" && newestCallObject.State == "ended") {
                        console.log("Collapsing HubSpot widget after saving notes as per user setting");
                        cti.callCompleted({
                        engagementId: engagementIdRef.current,
                        hideWidget: true,
                      });
                    }
                }
        }, 500));}, "hb_loadingButton", 500, brand === "enreach" || brand === "summa" ? { position: "relative", width: "100%" } : { position: "relative", width: "100%", backgroundColor: "white" }, "scriptButton brandbutton", "outline-info")}
    </div>;
    const viewNoteButton = <Button onClick={() => {
        const matchObjectIdStr = localStorage.hb_selectedMatchObjectId;
        if (!matchObjectIdStr || isNaN(matchObjectIdStr)) {
            const url = `https://app-eu1.hubspot.com/calls/${localStorage.hb_portalId}/review/${engagementIdRef.current}`;
            window.open(url, '_blank')?.focus();
        }
        else {
            const matchObjectId = Number(matchObjectIdStr);
            const match = incomingMatches.filter(x => x.objectCoordinates.objectId == matchObjectId)[0]; // Should always contain exactly 1 match
            const associationType = match.callerIdType == "CONTACT" ? "0-1" : "0-2";
            const url = `https://app-eu1.hubspot.com/contacts/${localStorage.hb_portalId}/record/${associationType}/${matchObjectId}?engagement=${engagementIdRef.current}`;
            window.open(url, '_blank')?.focus();
        }
    }}
    className="scriptButton brandbutton" 
    style={{ position: "relative", width: "100%" }}
    variant={"outline-info"} 
    title={Translate("Open note in HubSpot")}>
        {Translate("View note")}
    </Button>
    
    const notesSaved = localStorage.getItem("hb_notesSaved") == "true";
    return (<div style={{ color: "black", fontSize: "16px", paddingBottom: "12px" }}>
            <div style={{ paddingTop: "12px" }}>{Translate("Notes")}{notesSaved ? "" : "*"}</div>
            <div>
                <TextArea id="callingnotes" 
                rows={6} 
                ref={textAreaRef} 
                onInput={() => { 
                    onChangeNotesCallback(textAreaRef.current?.value, setForceRepaint);
                    localStorage.setItem("hb_notesSaved", "false");
                }}
                style={{ width: "100%" }}
                defaultValue={previousNotes != undefined ? previousNotes : ""}/>
            </div>
            {/* -14px to account for padding */}
            <table style={{ position: "fixed", width: "calc(100% - 14px)", bottom: "7px" }}>
                <tbody>
                    <tr>
                        <td width={"50%"}>
                            {saveCallButton}
                        </td>
                        <td style={{width: "3px"}}></td>
                        <td width={"50%"}>
                            {viewNoteButton}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

function onChangeNotesCallback(newNotes: string | undefined, setForceRepaint: CallableFunction) {
    //BroadcastBuffered(hubspotBroadcastChannelEmbedded, "notesChanged", newNotes, "hb_stashedNotes", "hb_notesTimeoutId");
    localStorage.setItem("hb_stashedNotes", newNotes ?? "")
    localStorage.setItem("broadcast_hubspot_embedded", JSON.stringify({
        type: "notesChanged",
        timestamp: Date.now()
    }));
    setForceRepaint((forceRepaint: number) => forceRepaint + 1);
}

async function PressSaveCall(cti: any, incomingMatches: (ContactIdMatch | CompanyIdMatch)[], textAreaRef: RefObject<HTMLTextAreaElement>, engagementId: string, match = undefined) {
    const localStorageSnapshot = {
        hb_selectedMatchObjectId: localStorage.getItem("hb_selectedMatchObjectId"),
        hb_lastAssociatedCallId: localStorage.getItem("hb_lastAssociatedCallId"),
        hb_lastAssociatedObjectId: localStorage.getItem("hb_lastAssociatedObjectId"),
    };
    const notesSnapshot = textAreaRef.current ? textAreaRef.current.value : null;
    await PressButton(() => onSaveCall(cti, incomingMatches, notesSnapshot, engagementId, match, localStorageSnapshot), "hb_loadingButton");
}

function onSaveCall(cti: any, incomingMatches: (ContactIdMatch | CompanyIdMatch)[], textAreaContent: string | null, engagementId: string, match: any = undefined, localStorageSnapshot: any = null) {
    const portalId = localStorage.hb_portalId;
    if (!portalId) {
        console.error("Missing portalId, can't save notes");
        return;
    }
    
    const matchObjectIdStr = localStorageSnapshot != null ? localStorageSnapshot.hb_selectedMatchObjectId : localStorage.hb_selectedMatchObjectId;
    const lastAssociatedCallId = localStorageSnapshot != null ? localStorageSnapshot.hb_lastAssociatedCallId : localStorage.hb_lastAssociatedCallId;
    const lastAssociatedObjectId = localStorageSnapshot != null ? localStorageSnapshot.hb_lastAssociatedObjectId : localStorage.hb_lastAssociatedObjectId;

    var notes = textAreaContent != undefined ? textAreaContent : localStorage.getItem("hb_stashedNotes");
    if (notes == undefined) {
        console.error("Missing note text, saving empty note");
        notes = "";
    }

    var matchObjectId : any;
    if (match == undefined) {
        if (matchObjectIdStr && !isNaN(matchObjectIdStr)) {
            matchObjectId = Number(matchObjectIdStr);
            match = incomingMatches.filter(x => x.objectCoordinates.objectId == matchObjectId)[0]; // Should always contain exactly 1 match
        }
    }
    else
        matchObjectId = match.objectCoordinates.objectId;

    if (match == undefined) {
        console.log("Selected contact/company is missing or has an invalid value, saving without association");
        UpdateNotes(engagementId, notes);
        localStorage.setItem("hb_notesSaved", "true");
        //hubspotBroadcastChannelEmbedded.postMessage("notesSaved");

        localStorage.setItem("broadcast_hubspot_embedded", JSON.stringify({
            type: "notesSaved",
            timestamp: Date.now()
        }));
        return;
    }
 
    const AssociateToObjectId = IsEmptyString(match.associatedcompanyid) ?  match.objectCoordinates.objectId : match.associatedcompanyid 
    if (lastAssociatedCallId != engagementId  
        || lastAssociatedObjectId != AssociateToObjectId) 
    {
        ChangeAssociationsHsObject(engagementId, match)
    }

    UpdateNotes(engagementId, notes);
    localStorage.setItem("hb_notesSaved", "true");
    //hubspotBroadcastChannelEmbedded.postMessage("notesSaved");

    localStorage.setItem("broadcast_hubspot_embedded", JSON.stringify({
        type: "notesSaved",
        timestamp: Date.now()
    }));
}

function UpdateNotes(engagementId: string, notes: string) {
    const bodyNotes = {
        portalId: localStorage.hb_portalId,
        callId: engagementId,
        notes: notes,
    }
    const config = { headers: { Authorization: `Bearer ${localStorage.getItem("securitytoken")}` } };
    axios.post(proxyUrl + "/updateNotes", bodyNotes, config)
    .then(response => {
        console.log("Notes succesfully updated");
    }).catch((error) => {
        console.log(error);
    });
}

export { 
    CallNotesElement,
    PressSaveCall
};